import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getDashboardToken } from '../utils/session';

interface SubmissionSuccessProps {
    submissionId: string;
    pdfs: Array<{ formName: string; downloadUrl: string }>;
    onStartNew: () => void;
}

const SubmissionSuccess: React.FC<SubmissionSuccessProps> = ({
    submissionId,
    pdfs,
    onStartNew,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    
    // Check if user came from admin (has dashboard token OR URL params indicate admin context)
    const params = new URLSearchParams(location.search);
    const hasDashboardAccess = !!getDashboardToken();
    const hasEditSubmissionId = !!params.get('editSubmissionId');
    const hasDraftId = !!params.get('draftId');
    const isFromAdmin = hasDashboardAccess && (hasEditSubmissionId || hasDraftId);

    const [countdown, setCountdown] = React.useState(5);

    const handleGoBackToAdmin = React.useCallback(() => {
        navigate('/admin/submissions');
    }, [navigate]);

    React.useEffect(() => {
        if (isFromAdmin) {
            if (countdown > 0) {
                const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
                return () => clearTimeout(timer);
            } else {
                handleGoBackToAdmin();
            }
        }
    }, [countdown, isFromAdmin, handleGoBackToAdmin]);
    return (
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12 text-center">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-8">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                    <svg
                        className="w-6 h-6 sm:w-8 sm:h-8"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                        />
                    </svg>
                </div>

                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Submission Successful!</h2>
                <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
                    All forms have been generated and securely stored.
                    <br />
                    <span className="text-xs sm:text-sm text-gray-400 break-all">Submission ID: {submissionId}</span>
                </p>

                <div className="bg-gray-50 rounded-lg p-3 sm:p-6 mb-6 sm:mb-8 text-left">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Generated Documents</h3>
                    <div className="space-y-2 sm:space-y-3">
                        {pdfs.map((pdf, index) => (
                            <div
                                key={index}
                                className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-white p-3 rounded border border-gray-200 gap-2 sm:gap-0"
                            >
                                <div className="flex items-center">
                                    <svg
                                        className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 mr-2 sm:mr-3 flex-shrink-0"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    <span className="text-sm sm:text-base text-gray-700 font-medium">{pdf.formName}</span>
                                </div>
                                <a
                                    href={pdf.downloadUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 font-medium text-xs sm:text-sm flex items-center w-full sm:w-auto justify-center sm:justify-start bg-blue-50 sm:bg-transparent py-2 sm:py-0 rounded sm:rounded-none"
                                >
                                    Download
                                    <svg
                                        className="w-4 h-4 ml-1"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                        />
                                    </svg>
                                </a>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                    {isFromAdmin ? (
                        <div className="text-center w-full max-w-sm mx-auto">
                            <p className="text-gray-700 font-medium mb-3">
                                Redirecting to Admin Dashboard in <span className="font-bold text-blue-600">{countdown}</span> seconds...
                            </p>
                            <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                <div 
                                    className="bg-blue-600 h-1.5 rounded-full transition-all duration-1000 ease-linear" 
                                    style={{ width: `${(countdown / 5) * 100}%` }}
                                ></div>
                            </div>
                        </div>
                    ) : (
                        <button
                            onClick={onStartNew}
                            className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm sm:text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full sm:w-auto"
                        >
                            Start New Submission
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SubmissionSuccess;
